import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMyContext } from "../../context/MyContext";
import { api } from "../../config/api";
import { Table, Tag } from "antd";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useState } from "react";
import { CheckCircle, ChevronDown, Clock, Info, Loader2, PlayCircle } from "lucide-react";
import Loader from "../shared/loader/loader";

const statusOptions = [
  { value: "pending", label: "Pending", icon: Clock },
  { value: "In Progress", label: "In Progress", icon: PlayCircle },
  { value: "completed", label: "Completed", icon: CheckCircle },
];

const StatusDropdown = ({ status, onChange, id, loading }) => {
  const [isOpen, setIsOpen] = useState(false);

  const getStatusColor = (statusValue) => {
    const lower = statusValue?.toLowerCase();
    switch (lower) {
      case "in progress":
        return {
          backgroundColor: "#fff9e0",
          color: "#FFC93F",
          borderColor: "#FFC93F",
        };
      case "completed":
        return {
          backgroundColor: "#dcfce7",
          color: "#166534",
          borderColor: "#86efac",
        };
      case "pending":
        return {
          backgroundColor: "#dbeafe",
          color: "#1e40af",
          borderColor: "#93c5fd",
        };
      default:
        return {
          backgroundColor: "#f3f4f6",
          color: "#374151",
          borderColor: "#d1d5db",
        };
    }
  };

  const isLoading = loading === id;

  const currentStatus = statusOptions.find(
    (opt) => opt.value.toLowerCase() === status?.toLowerCase()
  );
  const CurrentIcon = isLoading ? Loader2 : currentStatus?.icon || Info;

  const statusColors = getStatusColor(status);

  return (
    <div className="relative w-full max-w-xs">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 rounded-lg border-2 transition-all duration-200 flex items-center justify-between gap-2 hover:shadow-md"
        style={{
          backgroundColor: isLoading ? "#fef3c7" : statusColors.backgroundColor,
          color: isLoading ? "#d97706" : statusColors.color,
          borderColor: isLoading ? "#d97706" : statusColors.borderColor,
        }}
      >
        <div className="flex items-center gap-2">
          <CurrentIcon
            className={`${isLoading && "animate-spin"}`}
            style={{ width: "16px", height: "16px" }}
          />
          <span className="text-sm font-medium capitalize truncate">
            {isLoading ? "Please wait..." : status || "Select Status"}
          </span>
        </div>
        <ChevronDown
          style={{
            width: "16px",
            height: "16px",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms",
          }}
        />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute z-20 min-w-[250px] mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-80 overflow-y-auto">
            {statusOptions.map((option) => {
              const Icon = option.icon;
              const isSelected =
                option.value.toLowerCase() === status?.toLowerCase();

              return (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(id, option.value);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                  style={{
                    backgroundColor: isSelected ? "#dbeafe" : undefined,
                    color: isSelected ? "#2563eb" : undefined,
                  }}
                >
                  <Icon size={20} />
                  <span
                    className={`text-sm font-medium whitespace-nowrap ${
                      isSelected ? "text-blue-600" : "text-gray-700"
                    }`}
                  >
                    {option.label}
                  </span>
                  {isSelected && (
                    <CheckCircle className="w-4 h-4 ml-auto text-blue-600" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
const AssignedTodos = () => {
  const { user, token, isAuthenticated } = useMyContext();
  const queryClient = useQueryClient();
  const [localTodos, setLocalTodos] = useState([]);

  const { data, isLoading } = useQuery(
    ["assigned-todos", user?.uid],
    async () => {
      if (!token) throw new Error("No token available");
      const res = await api.get(`/todos/user/${user?.uid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return sortTodosByDate(res.data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!user && !!token && isAuthenticated,
      onSuccess: (data) => {
        setLocalTodos(data);
      },
      onError: (error) => {
        console.error("Error fetching assigned todos:", error);
        toast.error("Failed to load assigned todos. Please try again.");
      },
    }
  );

  const updateStatusMutation = useMutation(
    ["task"],
    async (data) => {
      await api.put(`/todos/status/${data?.id}/${data?.status}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    {
      onMutate: (variables) => {
        setLocalTodos((prev) =>
          sortTodosByDate(
            prev.map((todo) =>
              todo._id === variables.id
                ? { ...todo, status: variables.status }
                : todo
            )
          )
        );
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["assigned-todos", user?.uid]);
        queryClient.invalidateQueries(["todos"]); // optional: keep admin view in sync
        toast.success("Status updated!");
      },
      onError: () => {
        queryClient.invalidateQueries(["assigned-todos", user?.uid]);
        toast.error("Failed to update status. Please try again.");
      },
    }
  );

  const handleStatusChange = async (id, status) => {
    updateStatusMutation.mutate({ id, status });

    if (status.toLowerCase() === "completed") {
      try {
        await api.post("/notifications", {
          userId: user?.uid,
          title: "Task Completed",
          message: `The task with ${user.name} has been marked as completed.`,
          linkText: "View Todo",
          link: `/todo`,
          role: "admin",
          type: "task-completed",
        });
        toast.success("Notification sent for task completion!");
      } catch (error) {
        console.error("Error sending notification:", error);
        toast.error("Failed to send notification.");
      }
    }
  };

  const sortTodosByDate = (todos) => {
    return [...todos].sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  if (!isAuthenticated) return <div>Loading...</div>;

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      render: (_, record) => dayjs(record?.date).format("DD MMM YYYY"),
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      render: (_, record) => dayjs(record?.dueDate).format("DD MMM YYYY"),
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (_, record) => record?.title || "-",
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, record) => (
        <StatusDropdown
          status={record?.status}
          onChange={handleStatusChange}
          id={record?._id}
          loading={updateStatusMutation.isLoading ? record?._id : null}
        />
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      render: (_, record) => (
        <Tag
          className={`capitalize text-white border-none px-2 w-fit flex items-center justify-center rounded-full ${
            record?.priority === "Normal"
              ? "bg-green-500"
              : record?.priority === "Medium"
              ? "bg-yellow-500"
              : record?.priority === "High"
              ? "bg-red-500"
              : "bg-red-900"
          }`}
        >
          {record?.priority}
        </Tag>
      ),
    },
  ];

  return (
    <div className="flex flex-col">
      {isLoading ? (
        <Loader />
      ) : localTodos.length > 0 ? (
        <Table columns={columns} dataSource={localTodos} loading={isLoading} />
      ) : (
        <div className="text-center py-8 text-gray-500">
          No assigned todos found.
        </div>
      )}
    </div>
  );
};

export default AssignedTodos;